import { DEFAULT_EVENT_TYPES } from 'src/services/constants';
import errorService from 'src/services/error';

/**
 * Applies filters for the events feed.
 *
 * @param {import('src/types/events-store').EventsStoreActionContext} context
 * @param {{ dateRange: [string, string] | null; driverKey: string; types: import('src/types/events-feed').EventType[]; vehicleKey: string }} filters
 */
export async function applyFilters(context, { dateRange, driverKey, types, vehicleKey }) {
  context.commit('setDateRangeFilter', dateRange);
  context.commit('setTypeFilter', types);
  context.commit('setVehicleFilter', vehicleKey);
  context.commit('setDriverFilter', driverKey);
  context.dispatch('resetEvents');

  context.commit('setIsApplying', true);
  await context.dispatch('getEvents');
  context.commit('setIsApplying', false);
}

/**
 * Clears all filters used for the events feed.
 *
 * @param {import('src/types/events-store').EventsStoreActionContext} context
 */
export async function clearFilters(context) {
  context.commit('setDateRangeFilter', null);
  context.commit('setTypeFilter', []);
  context.commit('setVehicleFilter', '');
  context.commit('setDriverFilter', '');
  context.dispatch('resetEvents');

  context.commit('setIsApplying', true);
  await context.dispatch('getEvents');
  context.commit('setIsApplying', false);
}

/**
 * Retrieves a single event by key.
 *
 * @param {import('src/types/events-store').EventsStoreActionContext} context
 * @param {string} eventKey
 */
export async function getEvent(context, eventKey) {
  context.commit('setIsLoadingSingleEvent', true);

  /** @type {import('src/types/events-feed').EventsFeedData | import('src/models/ErrorPayload').default} */
  const data = await context.rootState.app.broker.fetchAndTransform({
    fn: 'getEvent',
    params: eventKey,
  });

  if (data.error) {
    errorService.log(new Error(data.error));
  } else {
    context.commit('setSingleEvent', data);
  }

  context.commit('setIsLoadingSingleEvent', false);

  return data;
}

/**
 * Retrieves events for the current user.
 *
 * @param {import('src/types/events-store').EventsStoreActionContext} context
 * @param {{ init?: boolean }} payload
 */
export async function getEvents(context, payload = {}) {
  const { init = false } = payload;

  context.commit('setIsLoading', true);

  if (init) {
    await context.dispatch('resetEvents');
  }

  // Map date range to before/after
  let after;
  let before;
  if (context.state.dateRangeFilter) {
    [after, before] = context.state.dateRangeFilter;
  }

  let types = [...context.state.typeFilter];
  if (types.length === 0) {
    // Include all types of events if no filter is set
    types = [...DEFAULT_EVENT_TYPES];
  }
  if (types.includes('trip_hard_brake') && !types.includes('trip_severe_hard_brake')) {
    types.push('trip_severe_hard_brake');
  }

  /** @type {import('src/types/events-feed').EventsFeedData | import('src/models/ErrorPayload').default} */
  const data = await context.rootState.app.broker.fetchAndTransform({
    fn: 'getEvents',
    params: {
      after,
      before,
      cursor: context.state.eventsCursor,
      types,
      vehicleKey: context.state.vehicleFilter,
      driverKey: context.state.driverFilter,
    },
  });

  let { cursor = '', events = [] } = data;

  if (data.error) {
    errorService.log(new Error(data.error));
  } else {
    if (cursor === '') {
      context.commit('setHasLoadedAll', true);
    }

    context.commit('insertEvents', events);
    context.commit('setEventsCursor', cursor);
    context.commit('setDidLoad', true);
  }

  context.commit('setIsLoading', false);

  return events;
}

/**
 * Clears events and resets the state of the events data load.
 *
 * @param {import('src/types/events-store').EventsStoreActionContext} context
 */
export async function resetEvents(context) {
  context.commit('setEvents', []);
  context.commit('setEventsCursor', '');
  context.commit('setHasLoadedAll', false);
  context.commit('setDidLoad', false);
  context.commit('setSelectedEvent', null);
}

/**
 * Selects the event with the given key.
 *
 * @param {import('src/types/events-store').EventsStoreActionContext} context
 * @param {import('src/types/events-feed').EventsFeedItem | null} event
 */
export async function selectEvent(context, event) {
  context.commit('setSelectedEvent', event);
}

/**
 * Selects a single event.
 *
 * @param {import('src/types/events-store').EventsStoreActionContext} context
 * @param {import('src/types/events-feed').EventsFeedItem | null} event
 */
export async function selectSingleEvent(context, event) {
  context.commit('setSingleEvent', event);
}

/**
 * Sets the height of the activity area (for content that sizes based on it)
 *
 * @param {import('src/types/events-store').EventsStoreActionContext} context
 * @param {number} height
 */
export async function setActivityAreaHeight(context, height) {
  context.commit('setActivityAreaHeight', height);
}
